"use client";

import { MessageCircle } from "lucide-react";
import { useRouter } from "next/navigation";
import { forwardRef, useTransition } from "react";
import { toast } from "sonner";
import { getOrCreateDirectChat } from "@/actions/chat";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Button } from "@/components/ui/button";
import { LiquidActionButton } from "@/components/ui/liquid-action-button";
import { useCurrentHref } from "@/hooks/use-current-href";
import { withFromParam } from "@/lib/back-nav";
import { cn } from "@/lib/utils";

type BuddyRequestTriggerProps = {
  isAlreadyBuddy: boolean;
  chatId?: number;
  targetUserId: string;
} & React.ComponentPropsWithoutRef<"button">; // Allow standard button props

export const BuddyRequestTrigger = forwardRef<
  HTMLButtonElement,
  BuddyRequestTriggerProps
>(
  (
    { isAlreadyBuddy, chatId, targetUserId, className, onClick, ...props },
    ref,
  ) => {
    const router = useRouter();
    const currentHref = useCurrentHref();
    const [isPending, startTransition] = useTransition();

    if (isAlreadyBuddy && chatId) {
      return (
        <Button
          ref={ref}
          asChild
          variant="ghost"
          size="icon"
          className={cn("text-text-primary hover:bg-surface-alt", className)}
          {...props}
        >
          <BackAwareLink href={`/chat/${chatId}`}>
            <MessageCircle className="size-6" />
          </BackAwareLink>
        </Button>
      );
    }

    const handleChatClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      startTransition(async () => {
        const result = await getOrCreateDirectChat(targetUserId);
        if (result.error) {
          toast.error(result.error);
          return;
        }
        if (result.data) {
          router.push(withFromParam(`/chat/${result.data.id}`, currentHref));
        }
      });
    };

    if (isAlreadyBuddy) {
      return (
        <Button
          ref={ref}
          variant="ghost"
          size="icon"
          className={cn("text-text-primary hover:bg-surface-alt", className)}
          onClick={handleChatClick}
          disabled={isPending}
          {...props}
        >
          <MessageCircle className="size-6" />
        </Button>
      );
    }

    // Pending states are NOT rendered here. `ConnectButton` is the single
    // component for every not-yet-connected state across the app, and it is
    // the one that knows how to withdraw a request — this used to render a
    // dead, disabled "Request sent" button with no way back out of it.
    return (
      <LiquidActionButton
        ref={ref}
        onClick={onClick}
        className={cn("rounded-full px-6 py-2.5 text-sm", className)}
        {...props}
      >
        <span className="text-xs font-black tracking-wider text-white uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
          Add as buddy
        </span>
      </LiquidActionButton>
    );
  },
);
BuddyRequestTrigger.displayName = "BuddyRequestTrigger";
